import { ButtonGroup, FadeIn } from "@xola/ui-kit";
import { JsDateRange, now } from "common";
import React, { useState } from "react";
import { Delegate, DelegateRole, PaymentMethod, Seller, Source, SourceCopy } from "types";
import shallow from "zustand/shallow";
import { queryClient } from "../../../api/queryClient";
import { useSeller } from "../../../hooks/useAccount";
import { useCollection } from "../../../hooks/useCollection";
import { filterDelegates } from "../../../models/Delegate";
import { getDefaultPaymentMethods, PaymentMethodInfo } from "../../../models/Payment";
import { getInitialDate } from "../../../models/Reports";
import { AnalyticsMode } from "../../../stores/reports/analyticsFilterStore";
import { useEarningsReportFilterStore } from "../../../stores/reports/earningsReportFilterStore";
import { ListFilter } from "../../Dashboard/Filters/ListFilter";
import { ItemSummary } from "../AnalyticsProductPicker";
import { ReportDatePicker } from "../ReportDatePicker";

export const EarningsReportFilter = () => {
    const seller = useSeller();
    const { isLoading: isFetchingDelegates } = useCollection<Delegate>(["users", seller.id, "delegates"]);

    const [from, to] = useEarningsReportFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const [setFrom, setTo] = useEarningsReportFilterStore((state) => [state.setFrom, state.setTo], shallow);
    const [filters, setFilters] = useEarningsReportFilterStore((state) => [state.filters, state.setFilters], shallow);

    const [value, setValue] = useState(getInitialDate(from, to));
    const handleDateChange = (dateValue: JsDateRange) => {
        if (dateValue.from && dateValue.to) {
            setFrom(now(dateValue.from));
            setTo(now(dateValue.to));
        }

        setValue(dateValue);
    };

    const activeMode = filters.mode === AnalyticsMode.RECOGNIZED ? 0 : 1;
    const handleModeChange = (activeIndex: number) => {
        const mode = activeIndex === 0 ? AnalyticsMode.RECOGNIZED : AnalyticsMode.CASH_FLOW;
        setFilters({ mode });
    };

    return (
        <div className="flex flex-row items-center justify-between border-b border-gray-light px-6 py-3">
            <div className="flex w-full flex-row items-center justify-between">
                <div
                    className="flex w-full flex-row items-center justify-start space-x-1 xl:space-x-4"
                    data-testid="earning-report-filter"
                >
                    <div>
                        <ReportDatePicker value={value} from={from} to={to} max={now()} onChange={handleDateChange} />
                    </div>

                    <div>
                        <ListFilter
                            label="Payment Method"
                            title="Filter Methods"
                            target={ItemSummary}
                            fetch={fetchPaymentMethods}
                            value={filters.paymentMethods}
                            onChange={(paymentMethods) => setFilters({ paymentMethods })}
                        />
                    </div>
                    {!isFetchingDelegates && (
                        <FadeIn>
                            <ListFilter
                                label="Reservationist"
                                title="Reservationists"
                                target={ItemSummary}
                                fetch={fetchDelegates}
                                value={filters.reservationist}
                                onChange={(reservationist) => setFilters({ reservationist })}
                            />
                        </FadeIn>
                    )}
                </div>
                <div>
                    <ButtonGroup className="align-top" size="medium" value={activeMode} onChange={handleModeChange}>
                        <ButtonGroup.Button>Recognized Earnings</ButtonGroup.Button>
                        <ButtonGroup.Button>Cash Flow</ButtonGroup.Button>
                    </ButtonGroup>
                </div>
            </div>
        </div>
    );
};

const fetchPaymentMethods = (seller: Seller) => {
    const methods = seller.paymentMethods ?? [];
    const customMethods: PaymentMethodInfo[] = methods.map((method) => {
        return { id: method.id ?? "N/A", name: method.name ?? method.id };
    });

    let defaultMethods = getDefaultPaymentMethods(seller);
    // Filter out ACH as it creates confusion for users when there are 2 Ach and ach filters
    defaultMethods = defaultMethods.filter((method) => method.id !== PaymentMethod.ach);
    return { data: [...defaultMethods, ...customMethods], isLoading: false };
};

const fetchDelegates = (seller: Seller) => {
    const delegates: Delegate[] = queryClient.getQueryData(["delegates", "collection"]) ?? [];
    const delegatesRoles = new Set([
        DelegateRole.RESERVATION_LITE,
        DelegateRole.RESERVATION,
        DelegateRole.SELLER_ADMIN,
        DelegateRole.BOOKING_ENGINE,
    ]);

    const filteredDelegates = filterDelegates(delegates, delegatesRoles);
    const staticSources = [Source.CHECKOUT, Source.GIFT, Source.KIOSK].map((source) => ({
        id: source,
        name: SourceCopy[source],
    }));

    const sellerData = { id: seller.id, name: seller.name };
    const data = [
        sellerData,
        ...(filteredDelegates.map((d) => ({ id: d.user.id, name: d.user.name })) ?? []),
        ...staticSources,
    ];

    return { data, isLoading: false };
};
